import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { FragmentOf } from '@eluve/graphql.tada';

import {
  getTemplateBundleDisplayName,
  templateBundleListItemFragment,
} from '../../../template-bundles/template-bundle.operations';

import { AffectedUsersPreview } from './AffectedUsersPreview';
import { BulkResultSummary } from './BulkResultSummary';
import { getTenantUsersForBulkBundleQuery } from './bulk-default-bundle.operations';
import {
  type BulkTargetUser,
  useBulkUpdateDefaultBundle,
} from './useBulkUpdateDefaultBundle';

type TemplateBundleListItem = FragmentOf<typeof templateBundleListItemFragment>;

interface UpdateDoctorIdsDialogProps {
  tenantId: string;
  tenantName: string;
  bundles: TemplateBundleListItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NO_SELECTION = '';

const uuidSchema = z.string().uuid();

type ParsedTokens = {
  /** Lowercased, deduplicated tokens that pass `z.string().uuid()`. */
  validIds: string[];
  /** Trimmed, deduplicated tokens that fail UUID shape validation. */
  malformed: string[];
};

/**
 * Splits the pasted blob on whitespace, commas, semicolons, or pipes; trims;
 * dedupes case-insensitively; classifies each unique token as either a
 * well-formed UUID or malformed. Order is preserved by first appearance.
 */
const parseTokens = (raw: string): ParsedTokens => {
  const seen = new Set<string>();
  const validIds: string[] = [];
  const malformed: string[] = [];
  for (const token of raw.split(/[\s,;|]+/)) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    if (uuidSchema.safeParse(trimmed).success) {
      validIds.push(key);
    } else {
      malformed.push(trimmed);
    }
  }
  return { validIds, malformed };
};

export const UpdateDoctorIdsDialog: React.FC<UpdateDoctorIdsDialogProps> = ({
  tenantId,
  tenantName,
  bundles,
  open,
  onOpenChange,
}) => {
  const toast = useToast();

  const [pasted, setPasted] = useState<string>('');
  const [targetBundleId, setTargetBundleId] = useState<string>(NO_SELECTION);

  const { data, loading: loadingUsers } = useQuery(
    getTenantUsersForBulkBundleQuery,
    {
      variables: { tenantId },
      context: eluveAdminHasuraContext,
      skip: !open,
      fetchPolicy: 'cache-and-network',
    },
  );

  const tenantUsers = useMemo(
    () => data?.tenantsByPk?.tenantUsers ?? [],
    [data],
  );

  const tenantUserById = useMemo(() => {
    const map = new Map<string, (typeof tenantUsers)[number]>();
    for (const tu of tenantUsers) map.set(tu.userId.toLowerCase(), tu);
    return map;
  }, [tenantUsers]);

  const bundlesById = useMemo(() => {
    const map = new Map<string, TemplateBundleListItem>();
    bundles.forEach((b) => map.set(b.id, b));
    return map;
  }, [bundles]);

  const { validIds, malformed } = useMemo(() => parseTokens(pasted), [pasted]);

  const matchedUsers = useMemo(
    () =>
      validIds
        .map((id) => tenantUserById.get(id))
        .filter((tu): tu is (typeof tenantUsers)[number] => Boolean(tu)),
    [validIds, tenantUserById],
  );

  const unmatchedUuids = useMemo(
    () => validIds.filter((id) => !tenantUserById.has(id)),
    [validIds, tenantUserById],
  );

  const previewRows = useMemo(
    () =>
      matchedUsers.map((tu) => {
        const currentBundleId = tu.settings?.defaultTemplateBundleId ?? null;
        const currentBundle = currentBundleId
          ? (bundlesById.get(currentBundleId) ?? null)
          : null;
        return {
          userId: tu.userId,
          firstName: tu.user?.firstName ?? null,
          lastName: tu.user?.lastName ?? null,
          email: tu.user?.email ?? null,
          specialty: tu.user?.specialty ?? null,
          currentBundleName: currentBundle
            ? getTemplateBundleDisplayName(currentBundle)
            : null,
        };
      }),
    [matchedUsers, bundlesById],
  );

  const { status, results, run, reset } = useBulkUpdateDefaultBundle(tenantId);

  useEffect(() => {
    if (!open) {
      setPasted('');
      setTargetBundleId(NO_SELECTION);
      reset();
    }
  }, [open, reset]);

  const isRunning = status === 'running';
  const isDone = status === 'done';

  const canSubmit =
    !isRunning &&
    !isDone &&
    targetBundleId !== NO_SELECTION &&
    matchedUsers.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const targets: BulkTargetUser[] = matchedUsers.map((tu) => ({
      userId: tu.userId,
      firstName: tu.user?.firstName ?? null,
      lastName: tu.user?.lastName ?? null,
      email: tu.user?.email ?? null,
    }));
    const settled = await run(targets, targetBundleId);
    const failed = settled.filter((r) => !r.ok).length;
    const unmatchedCount = unmatchedUuids.length + malformed.length;
    if (failed === 0 && unmatchedCount === 0) {
      toast.success(`Updated default bundle for ${settled.length} user(s)`);
    } else if (failed === 0) {
      toast.success(
        `Updated ${settled.length} user(s); ${unmatchedCount} pasted ID(s) did not match`,
      );
    } else {
      toast.error(
        `Updated ${settled.length - failed}/${settled.length}; ${failed} failed, ${unmatchedCount} unmatched`,
      );
    }
  };

  // Block Escape/X dismissal mid-run: closing while in flight would reset the
  // hook's status/results to idle, but the per-row mutations would still settle
  // and write back to the (now stale) state. A subsequent run would race over
  // the same state and silently overwrite the first run's results.
  const handleOpenChange = (next: boolean) => {
    if (!next && isRunning) return;
    onOpenChange(next);
  };

  const totalParsed = validIds.length + malformed.length;
  const showUnresolved = unmatchedUuids.length > 0 || malformed.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="2xl">
        <DialogHeader>
          <DialogTitle>Update Default Bundle by Doctor IDs</DialogTitle>
          <DialogDescription>
            Tenant: <strong>{tenantName}</strong>
          </DialogDescription>
        </DialogHeader>

        <Stack gap={4} align="stretch">
          <Stack gap={1}>
            <Label htmlFor="doctor-ids-paste">Doctor user IDs</Label>
            <Textarea
              id="doctor-ids-paste"
              rows={5}
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="Paste Eluve user UUIDs separated by newlines, commas, or spaces"
              disabled={loadingUsers || isRunning || isDone}
            />
            <Text size="xs" color="supporting">
              Only IDs belonging to a tenant user in{' '}
              <strong>{tenantName}</strong> will resolve. Anything else is
              reported as unmatched.
            </Text>
          </Stack>

          {totalParsed > 0 && (
            <AffectedUsersPreview
              users={previewRows}
              showCurrentBundle
              emptyMessage={
                loadingUsers
                  ? 'Loading users...'
                  : 'None of the pasted IDs resolve to a user in this tenant.'
              }
            />
          )}

          {showUnresolved && (
            <Stack
              gap={2}
              className="max-h-48 overflow-y-auto rounded-md border border-border-warning-subtle bg-bg-warning-subtle p-3"
            >
              <Text size="xs" weight="semibold">
                Unmatched IDs ({unmatchedUuids.length + malformed.length})
              </Text>
              {malformed.length > 0 && (
                <Stack gap={0}>
                  <Text size="xs" color="supporting">
                    Not a valid UUID ({malformed.length})
                  </Text>
                  {malformed.map((token) => (
                    <Text key={`malformed-${token}`} inline size="xs">
                      <code>{token}</code>
                    </Text>
                  ))}
                </Stack>
              )}
              {unmatchedUuids.length > 0 && (
                <Stack gap={0}>
                  <Text size="xs" color="supporting">
                    No tenant user in this tenant ({unmatchedUuids.length})
                  </Text>
                  {unmatchedUuids.map((id) => (
                    <Text key={`unmatched-${id}`} inline size="xs">
                      <code>{id}</code>
                    </Text>
                  ))}
                </Stack>
              )}
            </Stack>
          )}

          <Stack gap={1}>
            <Label htmlFor="doctor-ids-target-bundle">Target bundle</Label>
            <Select
              value={targetBundleId || undefined}
              onValueChange={setTargetBundleId}
              disabled={matchedUsers.length === 0 || isRunning || isDone}
            >
              <SelectTrigger id="doctor-ids-target-bundle" className="w-full">
                <SelectValue placeholder="Select target bundle" />
              </SelectTrigger>
              <SelectContent>
                {bundles.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {getTemplateBundleDisplayName(b)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Stack>

          {isDone && <BulkResultSummary results={results} />}
        </Stack>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isRunning}
          >
            {isDone ? 'Close' : 'Cancel'}
          </Button>
          {!isDone && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={isRunning}
            >
              {isRunning
                ? 'Updating...'
                : `Confirm update of ${matchedUsers.length} user${
                    matchedUsers.length === 1 ? '' : 's'
                  }`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
